import type { billingFr } from './billing.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** English texts for billing (lot L01): invoices, transactions, payment methods. Same keys as `billing.fr.ts`. */
export const billingEn: DeepStrings<typeof billingFr> = {
  common: {
    created: 'Created',
    updated: 'Updated',
    unknown: 'Unknown',
    close: 'Close',
    allStatuses: 'All statuses',
    notConfigured: 'Not provided by the operator',
    forbidden: 'Refused: billing is reserved to the organisation’s administrators. Ask them for this right.',
  },
  providers: {
    manual: 'Manual (bank transfer, SEPA direct debit)',
    stripe: 'Stripe',
    platform: 'Platform',
    other: 'Other',
  },
  invoices: {
    title: 'Invoices',
    subtitle:
      'The monthly invoices of the organisation. An unpaid invoice is settled by SEPA direct debit or by bank transfer, with its number as reference.',
    searchPlaceholder: 'Filter by number…',
    columns: {
      number: 'Number',
      period: 'Period',
      status: 'Status',
      subTotal: 'Excl. tax',
      taxes: 'Taxes',
      total: 'Total incl. tax',
    },
    statuses: {
      UNPAID: 'Unpaid',
      PAID: 'Paid',
      CANCELLED: 'Cancelled',
      ISSUED: 'Credit note issued',
      REFUND_DUE: 'Refund due',
    },
    empty: {
      title: 'No invoice',
      description: 'The first invoice is issued at the end of the first month of use.',
    },
    download: 'Download PDF',
    downloadLabel: 'Download invoice {{id}} as PDF',
    downloadFailed: 'Download failed: {{detail}}',
    downloadUnavailable: 'Invoice PDF rendering is not enabled on this installation.',
    detail: {
      title: 'Invoice {{id}}',
      tabs: { summary: 'Summary', transactions: 'Payments' },
      from: 'Period start',
      to: 'Period end',
      transferReference: 'Transfer reference',
      transactionsHint: 'The payments made for this invoice.',
      noTransaction: 'No payment for this invoice',
    },
    pay: {
      action: 'Pay',
      title: 'Pay invoice {{id}}',
      description: 'Choose the payment method. A bank transfer stays pending until it is received.',
      field: 'Payment method',
      submit: 'Pay',
      done: 'Payment of invoice {{id}} started.',
      none: 'No valid payment method: add a SEPA mandate or the bank transfer to pay.',
      openTransaction: 'Open the transaction',
    },
    creditNote: {
      badge: 'Credit note',
      action: 'Cancel with a credit note',
      title: 'Cancel invoice {{id}}?',
      description:
        'An issued invoice is never deleted: a credit note numbered in the same sequence cancels it, with its signed PDF. The invoice’s open payments are cancelled. This cannot be undone.',
      descriptionPaid:
        'Invoice {{id}} is paid: the credit note cancelling it will be “refund due”; the refund happens outside graal. This cannot be undone.',
      submit: 'Issue the credit note',
      done: 'Credit note {{id}} issued: invoice {{invoice}} is cancelled.',
      failed: 'Cancellation failed: {{detail}}',
      detailTitle: 'Credit note {{id}}',
      credits: 'Cancels invoice',
      cancelledBy: 'Cancelled by credit note',
    },
    generate: {
      action: 'Generate the month’s invoice',
      title: 'Generate the invoice of a month',
      description:
        'Platform administrator: computes the invoice of organisation {{tenant}} for the chosen month, from its consumption.',
      field: 'Month',
      fieldHint: 'As YYYY-MM. Defaults to the previous month.',
      submit: 'Generate',
      done: 'Invoice {{id}} generated.',
      errors: { month: 'A month as YYYY-MM (for instance 2026-09).' },
    },
  },
  transactions: {
    title: 'Transactions',
    subtitle:
      'Each payment of an invoice. A bank transfer or direct debit stays “pending” until the operator records the collection.',
    searchPlaceholder: 'Filter by transaction, invoice or reference…',
    columns: {
      id: 'Transaction',
      invoice: 'Invoice',
      status: 'Status',
      provider: 'Provider',
      reference: 'Reference',
      total: 'Amount',
    },
    statuses: {
      scheduled: 'Scheduled',
      pending: 'Pending',
      commited: 'Committed',
      succeeded: 'Collected',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    empty: {
      title: 'No transaction',
      description: 'Paying an invoice creates a transaction.',
    },
    reference: 'Payment reference',
    referenceHint: 'To quote as the bank transfer label: it links the payment to the invoice.',
    detail: {
      title: 'Transaction',
      tabs: { summary: 'Summary' },
      paymentMethod: 'Payment method',
      initiator: 'Initiated by',
    },
    sepa: {
      action: 'SEPA direct debit file',
      title: 'SEPA direct debit file',
      description:
        'Platform administrator: the due direct debits of every organisation, in one pain.008 file for your bank. Each payment becomes “committed”; mark it received once the statement is read.',
      date: 'Requested collection date',
      dateHint: 'Defaults to two days from now.',
      submit: 'Generate the file',
      done: 'Direct debit file downloaded.',
      nothing: 'No direct debit to present.',
      failed: 'File failed: {{detail}}',
    },
    settle: {
      succeeded: 'Mark received',
      failedAction: 'Mark failed',
      confirm: {
        succeeded: {
          title: 'Mark the payment as received?',
          description:
            'Invoice {{invoice}} becomes “paid” and is emailed with its PDF. This cannot be undone.',
        },
        failed: {
          title: 'Mark the payment as failed?',
          description: 'Invoice {{invoice}} stays unpaid; a new payment can be made.',
        },
      },
      done: {
        succeeded: 'Payment marked received: the invoice is paid.',
        failed: 'Payment marked failed.',
      },
      forbidden: 'Refused: settling a transaction is reserved to the platform administrator.',
      failed: 'The transaction was not settled: {{detail}}',
    },
  },
  paymentMethods: {
    title: 'Payment methods',
    subtitle:
      'The favorite method settles the invoices. SEPA direct debit under a mandate or bank transfer, with no external service.',
    searchPlaceholder: 'Filter by holder, IBAN or mandate…',
    new: 'Add a payment method',
    newTitle: 'Add a payment method',
    newSubtitle: 'The first method added becomes the organisation’s favorite.',
    chooseKind: 'Kind of payment method',
    columns: {
      kind: 'Kind',
      detail: 'Account',
      mandate: 'Mandate',
      status: 'Status',
    },
    kinds: {
      sepa: 'SEPA direct debit',
      in_person: 'Bank transfer',
      credit_card: 'Card',
      trial: 'Trial',
      paypal: 'PayPal',
    },
    statuses: {
      succeeded: 'Valid',
      pending: 'Pending',
      failed: 'Invalid',
    },
    favorite: 'Favorite',
    setFavorite: 'Set as favorite',
    favoriteSet: '{{name}} is now the favorite method.',
    favoriteFailed: 'The favorite did not change: {{detail}}',
    transferDetail: 'Reference: the number of each invoice',
    signedAt: 'Signed on {{date}}',
    created: 'Payment method added.',
    deleted: '{{name}} deleted.',
    deleteTitle: 'Delete the payment method',
    deleteLabel: 'Delete {{name}}',
    confirmDelete: 'Delete {{name}}? Payments already made are not cancelled.',
    confirmDeleteFavorite:
      '{{name}} is the favorite method: the server will refuse to delete it. Choose another favorite first.',
    errors: {
      CAN_NOT_DELETE_FAVORITE: 'The favorite method cannot be deleted: choose another favorite first.',
      CAN_NOT_SET_FAVORITE: 'Only a valid payment method can become the favorite.',
    },
    empty: {
      title: 'No payment method',
      description: 'Add a SEPA direct debit mandate or the bank transfer to settle the invoices.',
    },
    choices: {
      sepa: {
        title: 'SEPA direct debit',
        description: 'You sign a mandate; the operator debits each invoice from your account.',
      },
      transfer: {
        title: 'Bank transfer',
        description: 'You transfer each invoice to the operator’s account, with its number as reference.',
      },
    },
    sepa: {
      mandateTitle: 'SEPA direct debit mandate',
      creditor: 'Creditor',
      identifier: 'SEPA creditor identifier (CI)',
      reference: 'Unique mandate reference (UMR)',
      paymentType: 'Payment type',
      recurrent: 'Recurrent',
      theCreditor: 'the creditor',
      mandateText:
        'By signing this mandate form, you authorise {{creditor}} to send instructions to your bank to debit your account, and your bank to debit your account in accordance with the instructions from {{creditor}}.',
      rights:
        'As part of your rights, you are entitled to a refund from your bank under the terms and conditions of your agreement with your bank. A refund must be claimed within 8 weeks starting from the date on which your account was debited.',
      fields: {
        holderName: 'Account holder',
        holderNameHint: 'The debtor’s name, as it appears on the bank details.',
        iban: 'IBAN',
        accept: 'I accept the SEPA direct debit mandate above',
        acceptHint: 'The acceptance date is recorded as the mandate signature date.',
      },
      errors: {
        iban: 'Invalid IBAN: check the country code, the check digits and the length.',
        accept: 'The mandate must be accepted to be recorded.',
      },
      submit: 'Sign the mandate',
    },
    transfer: {
      title: 'Operator’s bank transfer details',
      beneficiary: 'Beneficiary',
      iban: 'IBAN',
      bic: 'BIC',
      reference:
        'Reference to quote on each transfer: the invoice number (for instance GS-2026-0042). The payment is recorded on receipt.',
      submit: 'Add the bank transfer',
    },
    stripe: {
      title: 'Card payment via Stripe: not configured',
      description:
        'This installation collects through Stripe, but no publishable key is configured: a card cannot be entered here. Ask the operator to configure it.',
      cardTitle: 'Bank card',
      cardHint:
        'The card is entered in a secure Stripe field: its number never goes through graal, which keeps only the last four digits.',
      submit: 'Save the card',
      notReady: 'The card field is not ready.',
      refused: 'The card was refused.',
      failed: 'Card not saved: {{detail}}',
      loadFailed: 'Stripe.js could not be loaded: card payment is unavailable for now.',
    },
  },
  /** ADR 0089: the « Pricing » page — per-minute grid (SaaS) or internal chargeback grid (self-hosted). */
  pricing: {
    title: {
      saas: 'Per-minute pricing',
      selfHosted: 'Internal chargeback grid',
    },
    subtitle: {
      saas: 'The graal.systems price grid, in euros excluding VAT. What you see here is what the monthly invoice applies.',
      selfHosted:
        'The internal prices set by your platform administrator to charge consumption back to projects and departments. The publisher subscription is paid per vCPU, outside this grid.',
    },
    rule: 'Billed per started minute, from actual start to end, multiplied by the number of instances. Price of a minute = monthly price ÷ 43,200.',
    calculator: {
      title: 'Estimate a workload',
      description:
        'Pick an instance type, a number of instances and a duration. The total is computed by graal-api, with the same rule as costs and invoices.',
      type: 'Instance type',
      typePlaceholder: 'Pick a type',
      instances: 'Instances',
      minutes: 'Duration (minutes)',
      shortcuts: 'Duration shortcuts',
      oneHour: '1 h',
      eightHours: '8 h',
      oneDay: '1 d',
      thirtyDays: '30 d',
      duration: '{{minutes}} billed min ({{hours}} h)',
      perMinute: 'Per minute',
      cost: 'Cost excl. VAT',
      add: 'Add a workload',
      remove: 'Remove this workload',
      charge: 'Workload {{index}}',
      incomplete: 'Incomplete line: pick a type, at least one instance and one minute.',
      totalPerMinute: 'Cost per minute, all workloads',
      total: 'Total excl. VAT',
      exact: 'Exact amount: {{amount}}. The invoice rounds each instance type to the cent over the month.',
      source: 'Computed by graal-api (POST /estimations), like costs and invoices.',
      computing: 'Computing…',
      nothing: 'Add a complete workload to estimate its cost.',
      mismatch:
        'The server estimate does not match the grid shown: the grid may have changed. Reload the page.',
    },
    grid: {
      title: {
        saas: 'Instance type grid',
        selfHosted: 'Internal instance type grid',
      },
      description:
        'Per-minute prices rounded to 4 decimals for display; the computation keeps the exact price.',
      searchPlaceholder: 'Filter by type or family…',
      columns: {
        type: 'Instance type',
        family: 'Family',
        vcpu: 'vCPU',
        memory: 'Memory',
        gpu: 'GPU',
        perMinute: '€ excl. VAT / minute',
        perHour: '€ excl. VAT / hour',
        monthly: 'Stored monthly price',
      },
      unsized: 'Not in the instance type catalogue: size unknown.',
      empty: {
        saas: {
          title: 'No published price',
          description: 'The graal.systems grid is not set on this installation yet.',
        },
        selfHosted: {
          title: 'No internal price',
          description:
            'The platform administrator sets the chargeback grid (monthly price per instance type); nothing is charged back before that.',
        },
      },
    },
    families: {
      development: 'Development',
      general: 'General purpose',
      'compute-optimized': 'Compute',
      compute: 'Compute',
      'memory-optimized': 'Memory',
      memory: 'Memory',
      gpu: 'GPU',
    },
    others: {
      title: 'Other products in the grid',
      EGRESS: 'Egress traffic, per GB',
      LOGS: 'Logs, per GB',
      SUPPORT: 'Support, per unit',
    },
    gpus_one: '{{count}} GPU',
    gpus_other: '{{count}} GPUs',
    none: '—',
  },
};
